import styled from 'styled-components'

export type ScoreBand = 'high' | 'mid' | 'low'

// Mock-accurate band palette (BRAND-9489 standalone mock's `colorFor`):
// high = cyan #00b9d5, mid = amber #f5920b, low = red #fc5151. `high` maps
// onto theme.secondaryColor, which already resolves to that exact cyan hex;
// mid/low have no matching theme token (alertColor is a brighter yellow,
// errorColor is a duller red), so those two stay literal hexes rather than
// being coerced onto the wrong-looking token.
const bandColor = (theme: { secondaryColor: string }, band: ScoreBand): string => {
  switch (band) {
    case 'high':
      return theme.secondaryColor
    case 'mid':
      return '#f5920b'
    case 'low':
    default:
      return '#fc5151'
  }
}

// ScoreBreakdown is the "tucked card" from the BRAND-9489 standalone mock: it
// slides UP behind the AI Rationale banner's bottom edge (negative margin-top),
// so its top corners are square (only the bottom two are rounded) and the
// banner paints over its tucked-in top. The tuck is set up by the drawer, which
// wraps banner + card in a shared stacking context and gives the banner the
// higher z-index (see AgentSourcedCreatorDrawer.styled.ts ScoreSummaryStack /
// BannerLayer) — the card just claims z-index:1 here so it sits BEHIND.
//
// Border/radius/shadow map to design-system tokens, not re-typed hex: border
// 1px #e3e3e3 = theme.grey400Color; shadow colour rgba(77,77,77,0.10) where
// 77/77/77 is theme.grey900Color's own RGB triplet, written as the token + a
// hex alpha suffix (no shadow token exists in the theme, same as DrawerPanel's
// own hardcoded shadow, but the colour itself is still token-derived).
export const ScoreBreakdownRoot = styled.div`
  position: relative;
  z-index: 1;
  margin-top: -1.25rem;
  background: ${({ theme }) => theme.whiteColor};
  border: 0.0625rem solid ${({ theme }) => theme.grey400Color};
  border-radius: 0 0 0.75rem 0.75rem;
  box-shadow: 0 0.25rem 0.625rem ${({ theme }) => `${theme.grey900Color}1a`};
  overflow: hidden;
`

// Collapsed the content area has max-height:0 (nothing but the toggle strip
// peeks out below the banner); on expand it animates open. The content stays
// mounted so the height can transition — the padded inner element carries the
// spacing so it collapses cleanly to zero height when closed (padding on the
// animated wrapper itself would leave a residual strip).
export const ScoreBreakdownContent = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '125rem' : '0')};
  transition: max-height 0.32s ease;
`

// Top padding is 2rem, not a symmetric-looking 1.5rem, to counteract the
// card's -1.25rem tuck: that top slice of the box is hidden behind the AI
// banner, so the VISIBLE gap above the flag pills is padding-top minus the
// tuck (2rem - 1.25rem = 0.75rem) — matching the 0.75rem bottom padding
// below the last category row, so the tucked card reads as symmetric
// top-to-bottom (BRAND-9489).
export const ScoreBreakdownContentInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 2rem 1.25rem 0.75rem;
`

export const FlagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

// Solid-fill severity pills matching the BRAND-9489 UX mock: white bold label
// on a full-severity-colour fill, fully rounded, compact padding. The generic
// @tribegroup Pill couldn't be coerced into this look via its props (it hard-
// codes 0.5rem non-bold text with 1px tracking), so this is a colocated local
// pill. Colours are the mock's literal severity palette, not the semantic
// theme triad: theme.alertColor is a bright yellow with poor contrast against
// white text, so 'alert' stays a literal amber (#f5920b) rather than being
// coerced onto that token. 'error' is likewise a literal red (#fc5151),
// slightly brighter than theme.errorColor. 'default' still resolves to
// grey900Color, which already equals the mock's dark-grey hex exactly.
export type FlagScheme = 'error' | 'alert' | 'default'

const flagFill = (theme: { grey900Color: string }, scheme: FlagScheme): string => {
  switch (scheme) {
    case 'error':
      return '#fc5151'
    case 'alert':
      return '#f5920b'
    case 'default':
    default:
      return theme.grey900Color
  }
}

export const FlagPill = styled.span<{ $scheme: FlagScheme }>`
  display: inline-block;
  padding: 0.1875rem 0.625rem;
  border-radius: 62.5rem;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  color: ${({ theme }) => theme.whiteColor};
  background: ${({ theme, $scheme }) => flagFill(theme, $scheme)};
`

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export const CategoryRow = styled.div`
  padding: 0.875rem 1rem;
  border: 0.0625rem solid ${({ theme }) => theme.grey400Color};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.whiteColor};
`

// The entire header is a button so the whole row (label through chevron) is
// one click target for this category's own disclosure state.
export const CategoryHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  font-family: Rubik, verdana, sans-serif;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
    border-radius: 0.25rem;
  }
`

export const CategoryLabel = styled.span`
  flex: 1;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};
`

export const CategoryScoreCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

export const CategoryScoreTrack = styled.div`
  width: 4rem;
  height: 0.375rem;
  border-radius: 0.1875rem;
  background: ${({ theme }) => theme.grey200Color};
  overflow: hidden;
`

export const CategoryScoreFill = styled.div<{ $width: number; $band: ScoreBand }>`
  height: 100%;
  border-radius: inherit;
  width: ${({ $width }) => $width}%;
  background: ${({ theme, $band }) => bandColor(theme, $band)};
`

export const CategoryScore = styled.span<{ $band: ScoreBand }>`
  min-width: 1.5rem;
  font-weight: 600;
  font-size: 1rem;
  text-align: right;
  color: ${({ theme, $band }) => bandColor(theme, $band)};
`

export const CategorySummary = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.grey800Color};
`

// The toggle is a full-width strip pinned to the bottom of the card, its label
// + chevron right-aligned. It is the ONLY thing that peeks out below the banner
// when collapsed, so it carries extra TOP padding while closed (26/14) to give
// the peek strip visible height above/below the top edge; once open the
// padding tightens (13/15). No border-top (Ted flagged the mock's divider line
// as an unwanted seam above "Hide breakdown" — removed). Pink label =
// primaryColor (#f848c4), the same token the mock's literal pink resolves to.
export const ToggleButton = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
  width: 100%;
  border: none;
  background: transparent;
  padding: ${({ $open }) => ($open ? '0.8125rem 1.25rem 0.9375rem' : '1.625rem 1.25rem 0.875rem')};
  margin: 0;
  cursor: pointer;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.primaryColor};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: -0.125rem;
  }
`
